/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { DATE_FORMAT_V1 } from '@constants/date';
import { ICardForm, FormPayload } from './types';
import { observer } from 'mobx-react';
import { Card, Grid, TextField, Checkbox, Typography, Divider, Radio } from '@material-ui/core';
import {
  Controller,
  useFormContext,
  ControllerRenderProps,
  ControllerFieldState,
  UseFormStateReturn,
} from 'react-hook-form';

import useStyles from '@components/Sura/Stepper/OnlinePayment/Card/styles';
import { validate } from '@utils/openPay';
import { isEmailValid, isPhoneNumber } from '@utils/validators';
import useStore from '@hooks/useStore';
import { PaymentOption } from '@store/sura';
import { URLS } from '@constants/suraURLs';
import OPENPAY from '@assets/images/icons/logo_Openpay.png';
import AMEX from '@assets/images/icons/logo_Amex.png';
import VISA from '@assets/images/icons/logo_Visa.png';
import MASTERCARD from '@assets/images/icons/logo_Mastercard.png';
import OysterButton from '@components/common/Button';
import { formattedAmount } from '@utils/format';
import ConfirmationModal from './ConfirmationModal';
import Bubble from '@assets/images/bubble_2.png';

const isAmexCard = (cardValue: string) => {
  if (!cardValue) return false;
  return cardValue.length === 15 || cardValue.startsWith('34') || cardValue.startsWith('37');
};

const isVisaMastercardCard = (cardValue: string) => {
  if (!cardValue) return false;
  return cardValue.length === 16;
};

const CardForm: React.FC<ICardForm> = ({
  checkedPrivacyNotice,
  setCheckedPrivacyNotice,
  checkedSubscribeCard,
  setCheckedSubscribeCard,
  errorFields = {},
  interestFreeMonths,
  setInterestFreeMonths,
  optionSelectedMSI,
  setOptionSelectedMSI,
  handleOnlySubscribeCard,
  checkedSendEmail,
  setCheckedSendEmail,
  loading,
  methods,
}) => {
  const { sura, ui, links } = useStore();
  const css = useStyles({ isMobile: ui.isMobileScreen });
  const { control, watch, clearErrors } = useFormContext<FormPayload>();
  const [isFormValid, setIsFormValid] = useState(false);
  const isAmexMode = sura.paymentOption === PaymentOption.american;

  useEffect(() => {
    const checkFormValidity = () => {
      const values = methods.getValues();
      const isCvvValid = () => {
        const cvv = values.cvv2 || '';
        return isAmexMode ? cvv.length === 4 : cvv.length === 3;
      };

      const hasAllValues = Boolean(
        values.holderName &&
          values.cardNumber &&
          values.cardMonthExpiration &&
          values.cardYearExpiration &&
          values.payeeEmail &&
          values.payeePhone &&
          isCvvValid(),
      );

      setIsFormValid(hasAllValues);
    };

    const subscription = methods.watch(() => {
      checkFormValidity();
    });

    checkFormValidity();

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [methods, isAmexMode]);

  const isUSD = links.paymentLink.exchangeRate === 'USD';
  const year = new Date().getFullYear();
  const currentYear = year.toString().slice(2);
  const [paymentOptions, setPaymentOptions] = useState<any>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const isSubscription = links.paymentLink.isSubscription;
  const isOnlySubscription = links.paymentLink.sura.onlySubscription;

  useEffect(() => {
    // Configurar el modo de validación del formulario
    if (!methods.getValues().cardNumber) {
      methods.reset(
        {
          holderName: '',
          cardNumber: '',
          cardMonthExpiration: '',
          cardYearExpiration: '',
          cvv2: '',
          payeeEmail: '',
          payeePhone: '',
        },
        {
          keepDefaultValues: true,
        },
      );
    }
  }, []);

  useEffect(() => {
    setPaymentOptions(paymentPlanAvailable());
  }, [links]);

  useEffect(() => {
    // Reiniciar estados cuando el step cambie
    setCheckedSubscribeCard(false);
    setCheckedPrivacyNotice(false);
    setShowConfirmationModal(false);
    setCheckedSendEmail(false);
  }, [sura.step]);

  const handleCheckPrivacyNotice = () => {
    setCheckedPrivacyNotice(!checkedPrivacyNotice);
  };

  const handleCheckSendEmail = () => {
    setCheckedSendEmail(!checkedSendEmail);
  };

  const handleCheckedSubscribeCard = () => {
    if (isSubscription) {
      setCheckedSubscribeCard(!checkedSubscribeCard);
    } else {
      if (!checkedSubscribeCard) {
        setShowConfirmationModal(true);
      } else {
        setCheckedSubscribeCard(false);
      }
    }
  };

  const handleConfirmSubscription = () => {
    setShowConfirmationModal(false);
    setCheckedSubscribeCard(true);
  };

  const handleCancelSubscription = () => {
    setShowConfirmationModal(false);
  };

  const handleCheckInterestFreeMonths = () => {
    setInterestFreeMonths(!interestFreeMonths);
    setOptionSelectedMSI('');
  };

  const handleCheckSelectedMSI = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptionSelectedMSI(event.target.value);
  };

  const paymentPlanAvailable = () => {
    const total = Number(links.paymentLink.total);
    const options = [
      { months: 12, amount: total / 12 },
      { months: 9, amount: total / 9 },
      { months: 6, amount: total / 6 },
      { months: 3, amount: total / 3 },
    ];

    if (total >= 1200) return options;
    else if (total >= 900) return options.slice(1);
    else if (total >= 600) return options.slice(2);
    else if (total >= 300) return options.slice(3);
  };

  const CatalogOfMonths: React.FC = () => (
    <React.Fragment>
      <Grid>
        <Typography className={css.labelNotice}>Selecciona un plan de pagos</Typography>
        {paymentOptions.length > 0 &&
          paymentOptions.toReversed().map((option: any, index: number) => {
            if (index < 2)
              return (
                <div
                  key={index}
                  className={`${css.divMonth} ${
                    index === 0
                      ? 'divMonthTop'
                      : index === paymentOptions.length - 1
                      ? 'divMonthBottom'
                      : 'divCenter'
                  }`}
                >
                  <Radio
                    checked={optionSelectedMSI === option.months.toString()}
                    onChange={handleCheckSelectedMSI}
                    value={option.months.toString()}
                    classes={{
                      checked: css.checkedRadio,
                      root: css.rootRadio,
                    }}
                  />
                  <Typography
                    className={`${css.labelOption} ${
                      optionSelectedMSI === option.months.toString() ? 'selected' : 'notSelected'
                    }`}
                  >
                    {`${option.months} pagos de ${formattedAmount(
                      option.amount,
                      isUSD ? 'USD' : 'MXN',
                    )} ${isUSD ? 'USD' : 'MXN'}`}
                  </Typography>
                </div>
              );
          })}
      </Grid>
    </React.Fragment>
  );

  const FormForCard: React.FC = () => (
    <React.Fragment>
      <Grid>
        <Controller
          control={control}
          name="payeeEmail"
          rules={{ required: true, validate: { email: isEmailValid } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={'Correo electrónico'}
              id="payee-email-input"
              fullWidth
              required
              className={css.textfield}
              error={fieldState.isTouched && (fieldState.invalid || errorFields.payeeEmail)}
              helperText={fieldState.isTouched && fieldState.error ? fieldState.error.message : ''}
              InputProps={{
                className: css.input,
              }}
            />
          )}
        />
      </Grid>
      <Grid>
        <Controller
          control={control}
          name="payeePhone"
          rules={{ required: true, validate: { phone: isPhoneNumber } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={'Número de teléfono'}
              id="payee-phone-input"
              fullWidth
              required
              className={css.textfield}
              error={fieldState.isTouched && (fieldState.invalid || errorFields.payeePhone)}
              inputProps={{
                inputMode: 'numeric',
                mask: '+52 99 9999 9999',
                placeholder: 'Ingresa tu número a 10 dígitos',
              }}
              type="number"
              onInput={(e: any) => {
                e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 10);
              }}
              InputProps={{
                className: css.input,
              }}
            />
          )}
        />
      </Grid>
      <Grid>
        <Controller
          control={control}
          name="holderName"
          rules={{
            required: true,
            validate: (value) => {
              const payeeFirstName = value.split(' ')[0] ?? '';
              const payeeLastName = value.split(' ')[1] ?? '';
              return payeeFirstName.length > 1 && payeeLastName.length > 1 ? true : false;
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={'Nombre en la tarjeta'}
              id="holder-name-input"
              fullWidth
              required
              className={css.textfield}
              error={fieldState.invalid || errorFields.holderName}
              helperText={fieldState.invalid ? 'Debes ingresar nombre y apellido' : ''}
              onChange={(e) => {
                const textValue = e.target.value.replace(/[^A-Za-zÀ-ÿ\s´]/g, '');
                field.onChange(textValue);
              }}
              inputProps={{
                placeholder: 'Ingresa el nombre',
              }}
              InputProps={{
                className: css.input,
              }}
            />
          )}
        />
      </Grid>
      <Grid>
        <Controller
          control={control}
          name="cardNumber"
          rules={{
            required: true,
            validate: {
              card: validate.card,
              amexInVisaMastercard: (value) => {
                const isAmex = isAmexCard(value);
                const isVisaMastercardMode = !isAmexMode;
                return !(isAmex && isVisaMastercardMode);
              },
              visaMastercardInAmex: (value) => {
                const isVisaMastercard = isVisaMastercardCard(value);
                return !(isVisaMastercard && isAmexMode);
              },
            },
          }}
          render={({
            field: { value, onChange, onBlur },
            fieldState,
          }: {
            field: ControllerRenderProps<FormPayload, 'cardNumber'>;
            fieldState: ControllerFieldState;
            formState: UseFormStateReturn<FormPayload>;
          }) => {
            const getHelperText = () => {
              if (!fieldState.isTouched || !fieldState.invalid || !value) {
                return '';
              }
              const isAmex = isAmexCard(value);
              const isVisaMastercard = isVisaMastercardCard(value);
              if (isAmex && !isAmexMode) {
                return 'Tarjeta inválida, al parecer estás intentando pagar con AMEX, regresa y selecciona AMEX como método de pago';
              }
              if (isVisaMastercard && isAmexMode) {
                return 'Tarjeta inválida, al parecer estás intentando pagar con VISA o Mastercard, regresa y selecciona VISA o Mastercard como método de pago';
              }
              return 'Número de tarjeta inválido';
            };

            return (
              <TextField
                value={value || ''}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const numericValue = (inputValue || '').replace(/\D/g, '');
                  const formattedValue = numericValue.slice(
                    0,
                    sura.paymentOption === PaymentOption.visa_mastercard ? 16 : 15,
                  );
                  onChange(formattedValue);
                }}
                onBlur={onBlur}
                label={'Número de tarjeta'}
                id="card-number-input"
                fullWidth
                required
                className={css.textfield}
                error={errorFields.cardNumber || (fieldState.isTouched && fieldState.invalid)}
                helperText={getHelperText()}
                inputProps={{
                  inputMode: 'numeric',
                  placeholder: isAmexMode ? '0000 0000 0000 000' : '0000 0000 0000 0000',
                }}
                InputProps={{
                  className: css.input,
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid container className={css.divs} spacing={ui.isMobileScreen ? 0 : 2}>
        <Grid item xs={12} md={6}>
          <Controller
            control={control}
            name="cardMonthExpiration"
            rules={{
              required: true,
              validate: (value) => parseInt(value) <= 12 && parseInt(value) > 0,
            }}
            render={({ field: { value, onChange, onBlur }, fieldState }) => (
              <TextField
                value={value || ''}
                onChange={(e) => {
                  const inputValue = e.target.value.replace(/\D/g, '').slice(0, 2);
                  onChange(inputValue);
                }}
                onBlur={onBlur}
                label={'Mes de vencimiento'}
                id="expiry-month-input"
                size={'medium'}
                required
                className={css.textfield}
                error={
                  errorFields.cardMonthExpiration || (fieldState.isTouched && fieldState.invalid)
                }
                fullWidth
                helperText={fieldState.isTouched && fieldState.invalid ? 'Mes inválido' : ''}
                inputProps={{
                  inputMode: 'numeric',
                  placeholder: '00',
                }}
                InputProps={{
                  className: css.input,
                }}
                type="number"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={control}
            name="cardYearExpiration"
            rules={{
              required: true,
              validate: {
                yearValid: (value) => {
                  const yearValue = parseInt(value);
                  const currentYearValue = parseInt(currentYear);
                  return !isNaN(yearValue) && yearValue >= currentYearValue;
                },
              },
            }}
            render={({ field: { onChange, value, onBlur }, fieldState }) => (
              <TextField
                value={value || ''}
                onChange={(e) => {
                  const inputValue = e.target.value.replace(/\D/g, '').slice(0, 2);
                  onChange(inputValue);
                }}
                onBlur={onBlur}
                label={'Año de vencimiento'}
                id="expiry-year-input"
                size={'medium'}
                required
                className={css.textfield}
                error={
                  errorFields.cardYearExpiration || (fieldState.isTouched && fieldState.invalid)
                }
                helperText={fieldState.isTouched && fieldState.invalid ? 'Año inválido' : ''}
                inputProps={{
                  inputMode: 'numeric',
                  placeholder: '00',
                }}
                InputProps={{
                  className: css.input,
                }}
                type="number"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid>
        <Controller
          control={control}
          name="cvv2"
          rules={{
            required: true,
            validate: (value, formValues) => validate.cvv(value, formValues.cardNumber),
          }}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextField
              value={value || ''}
              onChange={(e) => {
                const newValue = e.target.value
                  .replace(/\D/g, '')
                  .slice(0, sura.paymentOption === PaymentOption.visa_mastercard ? 3 : 4);
                onChange(newValue);
              }}
              onBlur={onBlur}
              label={'Código de seguridad'}
              id="cvv-input"
              fullWidth
              autoComplete="new-password"
              required
              type="password"
              className={css.textfield}
              error={errorFields.cvv2 || (fieldState.isTouched && fieldState.invalid)}
              helperText={fieldState.isTouched && fieldState.invalid ? 'CVV inválido' : ''}
              inputProps={{
                inputMode: 'numeric',
                placeholder: sura.paymentOption === PaymentOption.visa_mastercard ? '000' : '0000',
                autoComplete: 'new-password',
                name: 'cvv-no-autofill',
              }}
              InputProps={{
                className: css.input,
              }}
            />
          )}
        />
      </Grid>
      {links.paymentLink.isAvailableInterestFreeMonths && Number(links.paymentLink.total) > 300 && (
        <Grid>
          <div className={css.divNotice}>
            <Checkbox
              classes={{
                checked: css.checked,
                root: css.root,
              }}
              onChange={handleCheckInterestFreeMonths}
              checked={interestFreeMonths}
            />
            <Typography className={css.labelNotice}>Pagar a meses sin intereses</Typography>
          </div>
          {interestFreeMonths && <CatalogOfMonths />}
          <Divider variant="inset" className={css.divid} />
        </Grid>
      )}
    </React.Fragment>
  );

  const FormForClabeAccount: React.FC = () => (
    <React.Fragment>
      <Grid>
        <Controller
          control={control}
          name="clabeAccount"
          rules={{ required: true, validate: validate.card }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={'Número de cuenta clabe'}
              id="clabe-account-input"
              fullWidth
              required
              className={css.textfield}
              error={errorFields.clabeAccount || fieldState.invalid}
              inputProps={{
                inputMode: 'numeric',
                placeholder: '0000 0000 0000 0000',
              }}
              InputProps={{
                className: css.input,
                type: 'number',
              }}
              type="number"
              onInput={(e: any) => {
                e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 16);
              }}
            />
          )}
        />
      </Grid>
      <Grid>
        <Controller
          control={control}
          name="bankName"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={'Banco'}
              id="bank-name-input"
              fullWidth
              required
              className={css.textfield}
              error={errorFields.bankName || fieldState.invalid}
              inputProps={{
                placeholder: 'Nombre de banco',
              }}
              InputProps={{
                className: css.input,
              }}
            />
          )}
        />
      </Grid>
    </React.Fragment>
  );

  const BottomCommonFormInfo: React.FC = () => (
    <div style={{ marginTop: 20 }}>
      {/* Eliminar validación isOnlySubscription cuando la domiciliación se habilite */}
      {links.paymentLink.allowDomiciliation && (
        <div className={css.divNotice}>
          <Checkbox
            classes={{
              checked: css.checked,
              root: css.root,
            }}
            checked={checkedSubscribeCard}
            onChange={handleCheckedSubscribeCard}
          />
          <Typography className={css.labelNotice}>
            Acepto el{' '}
            <a
              target="_blank"
              rel="noreferrer noopener"
              href={URLS.PAYMENT_AVISO_PAGO}
              className={css.link}
            >
              <span className={css.labelSpan}>Aviso de pago</span>,
            </a>{' '}
            cargo recurrente y renovación a mi tarjeta de crédito/débito para el pago de esta
            póliza.
          </Typography>
        </div>
      )}
      <div className={css.divNotice}>
        <Checkbox
          classes={{
            checked: css.checked,
            root: css.root,
          }}
          checked={checkedPrivacyNotice}
          onChange={handleCheckPrivacyNotice}
        />
        <Typography className={css.labelNotice}>
          Acepto el{' '}
          <a
            target="_blank"
            rel="noreferrer noopener"
            href={URLS.PAYMENT_AVISO_PRIVACIDAD}
            className={css.link}
          >
            <span className={css.labelSpan}>Aviso de privacidad</span>
          </a>{' '}
          y los{' '}
          <a
            target="_blank"
            rel="noreferrer noopener"
            href={URLS.USO_MEDIOS_ELECTRONICOS}
            className={css.link}
          >
            <span className={css.labelSpan}>Términos y condiciones de uso</span>
          </a>{' '}
          de medios electrónicos.
        </Typography>
      </div>
      <div className={css.divNotice}>
        <Checkbox
          checked={checkedSendEmail}
          onChange={handleCheckSendEmail}
          classes={{
            checked: css.checked,
            root: css.root,
          }}
        />
        <Typography className={css.labelNotice}>
          Acepto el envío de liga de descarga de la documentación vía correo electrónico.
        </Typography>
      </div>
      <Divider variant="inset" className={css.divider} />
      <div className={css.divIcons}>
        <img src={OPENPAY} />
        {sura.paymentOption === PaymentOption.american && <img src={AMEX} />}
        {sura.paymentOption === PaymentOption.visa_mastercard && (
          <>
            <img src={VISA} />
            <img src={MASTERCARD} />
          </>
        )}
      </div>
    </div>
  );

  const BasicForm: React.FC = () => (
    <div className={css.divCard}>
      <Card className={css.card}>
        {sura.paymentOption === PaymentOption.clabe ? <FormForClabeAccount /> : <FormForCard />}
        <BottomCommonFormInfo />
      </Card>
    </div>
  );

  const SubscriptionForm: React.FC = () => (
    <div className={css.divCard}>
      <Grid container className={css.subscriptionCardContainer}>
        <Grid item xs={12} md={8}>
          <Grid container className={css.subscriptionCardLeftMiddleContainer}>
            <Grid item xs={12} md={4} className={css.subscriptionCardLeft}>
              <Typography className={css.labelSubscriptionDetailTitle}>Póliza</Typography>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>
                  Oficina, Ramo, Póliza
                </Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  <span style={{ fontWeight: 'bold' }}>
                    {links.paymentLink.sura.contracts[0].policyId}
                  </span>
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>
                  Nombre del contratante
                </Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {links.paymentLink.sura.contracts[0].customerName}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>Vigencia</Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  <span style={{ color: '#888B8D' }}>In.</span>{' '}
                  {moment(links.paymentLink.sura.contracts[0].invoices[0].issueDate).format(
                    DATE_FORMAT_V1,
                  )}
                </Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  <span style={{ color: '#888B8D' }}>Fin.</span>
                  {moment(links.paymentLink.sura.contracts[0].invoices[0].dueDate).format(
                    DATE_FORMAT_V1,
                  )}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>Recibo</Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {links.paymentLink.sura.contracts[0].invoices[0].name}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>
                  Fecha límite de pago
                </Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {moment(links.paymentLink.sura.contracts[0].invoices[0].expensionDate).format(
                    DATE_FORMAT_V1,
                  )}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>Importe MO</Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {formattedAmount(
                    Number(+links.paymentLink.sura.contracts[0].invoices[0].importMO / 100).toFixed(
                      2,
                    ),
                    'USD',
                  )}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>Moneda</Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {links.paymentLink.sura.contracts[0].invoices[0].currency}
                </Typography>
              </div>
              <div className={css.subscriptionCardLeftContent}>
                <Typography className={css.labelSubscriptionDetailSubTitle}>Importe MN</Typography>
                <Typography className={css.labelSubscriptionDetailContent}>
                  {formattedAmount(
                    Number(+links.paymentLink.sura.contracts[0].invoices[0].price / 100).toFixed(2),
                    links.paymentLink.sura.contracts[0].invoices[0].currency ? 'USD' : 'MXN',
                  )}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={8} className={css.subscriptionCardMiddle}>
              <label className={css.labelPayment}>
                <span className={css.labelExtraBold}>Información de la tarjeta</span>
              </label>
              <div className={css.subscriptionCardMiddleForm}>
                <FormForCard />
              </div>
              <BottomCommonFormInfo />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4} className={css.subscriptionCardRight}>
          <div className={css.subscriptionPaymentInformation}>
            <div>
              <img src={Bubble} className={css.bubbleImg} />
            </div>
            <Typography className={css.labelSubscriptionDetaiContent}>
              El cobro para validar un método de pago es una retención temporal de fondos en una
              tarjeta para verificar que la información de pago es correcta. Este proceso se conoce
              como verificación de pago y se realiza para evitar el fraude y proteger las
              transacciones
            </Typography>
            <OysterButton
              label="Domiciliar Póliza"
              loading={loading}
              onClick={() => handleOnlySubscribeCard()}
              className={css.subscribeButton}
              disabled={
                !checkedSubscribeCard ||
                !checkedPrivacyNotice ||
                !checkedSendEmail ||
                loading ||
                !isFormValid
              }
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
  return (
    <>
      {!isOnlySubscription && (
        <label className={css.labelPayment}>
          <span className={css.labelExtraBold}>
            Ingresa la Información de la{' '}
            {sura.paymentOption === PaymentOption.clabe ? 'Cuenta Clabe' : 'tarjeta'}
          </span>
        </label>
      )}
      {isOnlySubscription ? <SubscriptionForm /> : <BasicForm />}
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={handleCancelSubscription}
        onAccept={handleConfirmSubscription}
      />
    </>
  );
};

export default observer(CardForm);
